import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  Select,
  Stack,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { useLegalEntitySave } from "@/queries/use-legal-entities.query.ts";
import { useLocations } from "@/queries/use-locations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiLegalEntity, ApiLegalEntitySavePayload } from "@/types/legal-entity.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface LegalEntityEditModalProps extends ModalProps {
  legalEntityId: number;
  legalEntity?: ApiLegalEntity;
}

export function LegalEntityEditModal({
  legalEntityId,
  legalEntity,
  ...props
}: LegalEntityEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const requiredMessage = t("errors:field_required");
  const legalEntitySchema = z.object({
    title: z.string().min(1, requiredMessage),
    tax_id: z.string().nullable().optional(),
    currency_code: z.string().nullable().optional(),
    location_id: z.number({ error: requiredMessage }),
    standard_weekly_hours: z
      .number()
      .min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
      .max(168, t("api_errors:maximum_value_must", { maximum: 168 }))
      .nullable()
      .optional(),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(legalEntitySchema),
    defaultValues: {
      title: legalEntity?.title,
      tax_id: legalEntity?.tax_id,
      currency_code: legalEntity?.currency?.code,
      location_id: legalEntity?.location.id,
      standard_weekly_hours: legalEntity?.standard_weekly_hours ?? undefined,
    },
  });

  const legalEntitySave = useLegalEntitySave(control);
  const { data: locations } = useLocations();
  const { addRecent } = useRecent();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload: ApiLegalEntitySavePayload = {
        title: data.title,
        tax_id: data.tax_id,
        currency_code: data.currency_code,
        location_id: data.location_id,
        standard_weekly_hours: data.standard_weekly_hours ?? null,
      };
      const savedData = await legalEntitySave.mutateAsync({ id: legalEntityId, payload: payload });
      if (dirtyFields.currency_code && data.currency_code) {
        addRecent([{ type: ApiRecentKey.currency, values: [data.currency_code] }]);
      }
      if (legalEntityId == 0) {
        analyticsService.trackEvent(analyticEvents.legalEntities.created, {
          [analyticProperties.id]: savedData.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.legalEntities.edited, {
          [analyticProperties.id]: legalEntityId,
        });
      }
      close(id);
    } catch {
      return;
    }
  });
  return (
    <>
      <Modal
        {...props}
        title={t("core:legal_entity")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={legalEntitySave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />
          <Input
            label={t("common:tax_id")}
            type={"text"}
            {...register("tax_id", {
              value: undefined,
            })}
            error={errors.tax_id?.message}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <CurrencySelect
                label={t("common:currency")}
                emptyTitle={t("common:select")}
                searchable
                value={value}
                onChange={onChange}
                error={errors.currency_code?.message}
              />
            )}
            control={control}
            name={"currency_code"}
          />
          <Controller
            control={control}
            name="location_id"
            render={({ field: { value, onChange } }) => (
              <Select
                required
                label={t("common:location")}
                options={locations || []}
                emptyTitle={t("common:select")}
                value={value}
                onChange={onChange}
                searchable
                error={errors.location_id?.message}
              />
            )}
          />
          <Input
            label={t("core:standard_weekly_hours")}
            type={"number"}
            min={0}
            max={168}
            step={0.5}
            {...register("standard_weekly_hours", {
              setValueAs: (value) => (value === "" ? null : Number(value)),
            })}
            error={errors.standard_weekly_hours?.message}
          />
          <Box className={"py-2 text-sm text-secondary"}>
            {`${t("core:standard_weekly_hours_hint")} ${t(
              "core:standard_weekly_hours_inherit_org"
            )}`}
          </Box>
        </form>
      </Modal>
    </>
  );
}
